/**
 * TANSTACK QUERY - CENTRALIZED TIME CONFIGURATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This file centralizes all staleTime and gcTime (cache time) configurations
 * for TanStack Query across the application.
 *
 * TERMINOLOGY:
 * - staleTime: How long data is considered "fresh" before background refetch
 * - gcTime: How long inactive data stays in cache before garbage collection
 *           (formerly called "cacheTime" in older TanStack Query versions)
 */

// ═══════════════════════════════════════════════════════════════════════════
// STALE TIME CONSTANTS (Data Freshness)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Stale time presets for different data freshness requirements.
 * Use these constants instead of hardcoding millisecond values.
 */
export const STALE_TIME = {
  /**
   * REALTIME (0ms) - Always refetch on mount/focus
   * Use for: Availability, holds, check-in/out lists, room status
   */
  REALTIME: 0,

  /**
   * LOW_LATENCY (10s) - Very short freshness window
   * Use for: Calendar data, frequently changing aggregates
   */
  LOW_LATENCY: 10_000,

  /**
   * SHORT (30s) - Short freshness window
   * Use for: Housekeeping stats, user assignments, notifications
   */
  SHORT: 30_000,

  /**
   * NORMAL (1min) - Standard freshness for most data
   * Use for: Bookings list, tasks, guests, log entries
   */
  NORMAL: 60_000,

  /**
   * MEDIUM (2min) - Moderate freshness window
   * Use for: Staff lists, entity counts
   */
  MEDIUM: 2 * 60_000,

  /**
   * LONG (5min) - Extended freshness window
   * Use for: Master data, roles, property info, subscriptions
   */
  LONG: 5 * 60_000,

  /**
   * STATIC (30min) - Rarely changing data
   * Use for: Compliance data, access matrix, bed types
   */
  STATIC: 30 * 60_000,
};

// ═══════════════════════════════════════════════════════════════════════════
// GC TIME CONSTANTS (Cache Duration / Garbage Collection)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * GC time (garbage collection) presets for cache retention.
 * Controls how long inactive queries remain in memory.
 */
export const GC_TIME = {
  /**
   * NONE (0ms) - No caching
   * Use for: Mutations, one-time fetches, sensitive data
   */
  NONE: 0,

  /**
   * VERY_SHORT (10s) - Minimal caching
   * Use for: Availability windows, hold status
   */
  VERY_SHORT: 10_000,

  /**
   * SHORT (30s) - Brief caching
   * Use for: Hold data, ephemeral state
   */
  SHORT: 30_000,

  /**
   * NORMAL (1min) - Light caching
   * Use for: Session data, user-specific queries
   */
  NORMAL: 60_000,

  /**
   * MEDIUM (5min) - Standard cache retention
   * Use for: Most list queries, bookings, tasks
   */
  MEDIUM: 5 * 60_000,

  /**
   * LONG (10min) - Extended cache retention
   * Use for: Entity data, logs, staff details
   */
  LONG: 10 * 60_000,

  /**
   * EXTENDED (15min) - Long cache retention
   * Use for: Property data, roles, components
   */
  EXTENDED: 15 * 60_000,

  /**
   * STATIC (30min) - Very long cache retention
   * Use for: Master fields, access matrix, bed types
   */
  STATIC: 30 * 60_000,

  /**
   * VERY_LONG (1hr) - Maximum cache retention
   * Use for: Compliance data, rarely accessed reference data
   */
  VERY_LONG: 60 * 60_000,
};

// ═══════════════════════════════════════════════════════════════════════════
// QUERY PRESETS (Common Option Combinations)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Pre-configured option bundles for common query patterns.
 * Spread these into your API config options for consistency.
 *
 * @example
 * options: {
 *   ...QUERY_PRESETS.LIST,
 *   // Additional overrides if needed
 * }
 */
export const QUERY_PRESETS = {
  /**
   * REALTIME - Real-time data that must always be fresh
   * Use for: Availability checks, holds, today's check-ins/outs
   */
  REALTIME: {
    staleTime: STALE_TIME.REALTIME,
    gcTime: GC_TIME.MEDIUM,
    refetchOnWindowFocus: true,
  },

  /**
   * REALTIME_POLLING - Real-time with automatic polling
   * Use for: Live dashboards, availability during booking flow
   */
  REALTIME_POLLING: {
    staleTime: STALE_TIME.REALTIME,
    gcTime: GC_TIME.MEDIUM,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000, // Poll every minute
    keepPreviousData: true,
  },

  /**
   * LOW_LATENCY - Frequently changing data with short cache
   * Use for: Calendar, stats that update often
   */
  LOW_LATENCY: {
    staleTime: STALE_TIME.LOW_LATENCY,
    gcTime: GC_TIME.NORMAL,
    refetchOnWindowFocus: false,
  },

  /**
   * SHORT - Short cache for semi-real-time data
   * Use for: Housekeeping status, assignments
   */
  SHORT: {
    staleTime: STALE_TIME.SHORT,
    gcTime: GC_TIME.MEDIUM,
    refetchOnWindowFocus: false,
  },

  /**
   * LIST - Standard list/table data
   * Use for: Bookings, tasks, guests, roles, logs
   */
  LIST: {
    staleTime: STALE_TIME.NORMAL,
    gcTime: GC_TIME.MEDIUM,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  },

  /**
   * DETAIL - Detail view for single items
   * Use for: getById queries, edit forms
   */
  DETAIL: {
    staleTime: STALE_TIME.NORMAL,
    gcTime: GC_TIME.MEDIUM,
    refetchOnWindowFocus: false,
    enabled: false, // Run manually when ID is available
  },

  /**
   * STATIC - Rarely changing reference data
   * Use for: Master data, roles, property info, bed types
   */
  STATIC: {
    staleTime: STALE_TIME.LONG,
    gcTime: GC_TIME.EXTENDED,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  },

  /**
   * IMMUTABLE - Configuration that almost never changes
   * Use for: Access matrix, compliance settings
   */
  IMMUTABLE: {
    staleTime: STALE_TIME.STATIC,
    gcTime: GC_TIME.STATIC,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  },

  /**
   * MUTATION - For mutation configs (no caching needed)
   * Use for: Create/update/delete operations
   */
  MUTATION: {
    staleTime: STALE_TIME.REALTIME,
    gcTime: GC_TIME.NONE,
  },
};
